import { useState, useEffect } from 'react';
import type { Lead } from '@/mocks/admin';
import { markAsInteressado } from '@/services/leads-site';
import tr60dHeaderB64 from '@/assets/tr60d_header_b64.txt?raw';

interface GerarDiagnosticoModalProps {
  lead: Lead;
  onClose: () => void;
}

// ── NÍVEIS DO DIAGNÓSTICO ──────────────────────────────────────────────
const nivelConfig: Record<number, {
  titulo: string; subtitulo: string; mensagem: string; problemas: string[];
  textColor: string; badgeBg: string; borderColor: string; bgCard: string; emoji: string; posicao: number;
}> = {
  1: {
    titulo: 'Negócio em Risco', subtitulo: 'Alta desorganização e dependência total do dono.',
    mensagem: 'Seu negócio possui gargalos críticos que limitam crescimento.',
    problemas: [
      'Fluxo de caixa descontrolado gerando crises constantes',
      'Vendas imprevisíveis sem processo definido',
      'Exposição jurídica elevada — sem contratos ou documentos organizados',
      'Tudo depende de você — sem delegação real',
    ],
    textColor: 'text-red-600', badgeBg: 'bg-red-100', borderColor: 'border-red-200', bgCard: 'bg-red-50', emoji: '🚨', posicao: 12,
  },
  2: {
    titulo: 'Negócio Desorganizado', subtitulo: 'Algumas áreas funcionam, mas sem estrutura clara.',
    mensagem: 'Você tem potencial de crescimento, mas falta estrutura.',
    problemas: [
      'Processos existem na cabeça, não no papel',
      'Equipe sem direção clara e retrabalho constante',
      'Contratos e documentos improvisados — risco jurídico latente',
      'Decisões tomadas no achismo sem dados concretos',
    ],
    textColor: 'text-orange-600', badgeBg: 'bg-orange-100', borderColor: 'border-orange-200', bgCard: 'bg-orange-50', emoji: '⚠️', posicao: 37,
  },
  3: {
    titulo: 'Negócio Estruturando', subtitulo: 'Já existe controle em algumas áreas.',
    mensagem: 'Seu negócio está no caminho certo, mas ainda existem gargalos.',
    problemas: [
      'Falta padronização para escalar sem perder qualidade',
      'Indicadores parciais dificultam decisões estratégicas',
      'Proteção jurídica parcial — ainda existem vulnerabilidades',
      'Automação ainda não está sendo aproveitada ao máximo',
    ],
    textColor: 'text-amber-600', badgeBg: 'bg-amber-100', borderColor: 'border-amber-200', bgCard: 'bg-amber-50', emoji: '📈', posicao: 62,
  },
  4: {
    titulo: 'Pronto para Escalar', subtitulo: 'Estrutura avançada com base sólida.',
    mensagem: 'Seu negócio possui boa base para crescimento acelerado.',
    problemas: [
      'Otimizar processos para suportar crescimento acelerado',
      'Implementar IA e automação para ganhar vantagem competitiva',
      'Blindagem jurídica completa para expansão segura',
      'Estruturar liderança para expansão sem depender do dono',
    ],
    textColor: 'text-emerald-600', badgeBg: 'bg-emerald-100', borderColor: 'border-emerald-200', bgCard: 'bg-emerald-50', emoji: '🚀', posicao: 88,
  },
};

// ── DIAGNÓSTICO POR ÁREA ────────────────────────────────────────────────
const areaDiagnosticoConfig: Record<string, {
  label: string; icon: string; cor: string; corBg: string; corText: string; corBar: string;
  diagnosticos: string[]; recomendacoes: string[]; nivelSeveridade: string[];
}> = {
  financeiro: {
    label: 'Organização Financeira', icon: 'ri-money-dollar-circle-line',
    cor: 'bg-emerald-500', corBg: 'bg-emerald-50', corText: 'text-emerald-700', corBar: '#10b981',
    diagnosticos: ['Sem controle financeiro — alto risco de crise de caixa','Controle informal — decisões baseadas em estimativas','Controle básico — falta visibilidade para crescer','Controle sólido — planejamento financeiro com visibilidade total'],
    recomendacoes: ['Implemente urgentemente um fluxo de caixa e separe finanças pessoais das empresariais.','Estruture um DRE simplificado e comece a registrar todas as entradas e saídas.','Avance para indicadores como margem de contribuição, ponto de equilíbrio e formação de preço.','Use Dashboard Financeiro e Assistentes Financeiros com IA para decisões mais rápidas.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  vendas: {
    label: 'Estrutura Comercial', icon: 'ri-hand-coin-line',
    cor: 'bg-orange-500', corBg: 'bg-orange-50', corText: 'text-orange-700', corBar: '#f97316',
    diagnosticos: ['Vendas no improviso — receita imprevisível e instável','Processo informal — depende do feeling de cada vendedor','Processo parcial — falta padronização e métricas','Estrutura comercial completa — vendas previsíveis e replicáveis'],
    recomendacoes: ['Crie um funil comercial adaptado à sua operação com etapas claras.','Documente scripts para abordagem, follow-up e fechamento.','Implemente um CRM simplificado e métricas de conversão.','Estruture uma rotina diária de prospecção com metas e acompanhamento.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  gestao: {
    label: 'Gestão e Estrutura Operacional', icon: 'ri-settings-3-line',
    cor: 'bg-sky-500', corBg: 'bg-sky-50', corText: 'text-sky-700', corBar: '#0ea5e9',
    diagnosticos: ['Sem processos — operação caótica e dependente do dono','Processos informais — conhecimento concentrado em pessoas','Processos parciais — falta documentação e padronização','Processos definidos — operação previsível e delegável'],
    recomendacoes: ['Mapeie as 3 atividades mais críticas e crie um passo a passo simples para cada.','Documente os processos existentes e defina responsáveis claros.','Crie um organograma funcional e implemente reuniões de acompanhamento de 15 minutos.','Automatize processos repetitivos e foque em liderança estratégica.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  indicadores: {
    label: 'Gestão e Estrutura Operacional', icon: 'ri-bar-chart-2-line',
    cor: 'bg-sky-500', corBg: 'bg-sky-50', corText: 'text-sky-700', corBar: '#0ea5e9',
    diagnosticos: ['Sem indicadores — decisões no escuro','Apenas faturamento — visão limitada do negócio','Indicadores parciais — falta visão integrada','Gestão orientada por dados com painel completo de KPIs'],
    recomendacoes: ['Comece com 3 indicadores: faturamento, ticket médio e número de clientes.','Adicione indicadores de custo, margem, ponto de equilíbrio e inadimplência.','Crie um painel semanal com os principais KPIs do negócio.','Implemente B.I. com indicadores para decisões em tempo real.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  dependencia: {
    label: 'Gestão e Estrutura Operacional', icon: 'ri-user-star-line',
    cor: 'bg-sky-500', corBg: 'bg-sky-50', corText: 'text-sky-700', corBar: '#0ea5e9',
    diagnosticos: ['Total dependência — negócio para sem o dono','Alta dependência — operação instável sem o dono','Dependência parcial — alguns processos funcionam sozinhos','Negócio autônomo — funciona como sistema independente do dono'],
    recomendacoes: ['Identifique as 5 tarefas que só você faz e comece a documentar e delegar uma por vez.','Treine um colaborador para assumir responsabilidades operacionais.','Crie um manual de operações e implemente reuniões de acompanhamento.','Foque em estratégia enquanto a operação flui sem sua presença diária.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  tecnologia: {
    label: 'Inteligência Artificial Aplicada ao Negócio', icon: 'ri-robot-2-line',
    cor: 'bg-rose-500', corBg: 'bg-rose-50', corText: 'text-rose-700', corBar: '#f43f5e',
    diagnosticos: ['Sem tecnologia — operação manual e lenta','Tecnologia básica — potencial desperdiçado','Automações parciais — ganhos limitados','IA implementada — vantagem competitiva real e processos automatizados'],
    recomendacoes: ['Comece com ferramentas gratuitas: Google Workspace, Trello e WhatsApp Business.','Implemente IA no atendimento ao cliente e geração automática de conteúdos.','Use IA para analisar dados e automatizar tarefas operacionais repetitivas.','Crie fluxos de automação e assistentes empresariais com IA.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  time_comercial: {
    label: 'Estrutura Comercial', icon: 'ri-team-line',
    cor: 'bg-orange-500', corBg: 'bg-orange-50', corText: 'text-orange-700', corBar: '#f97316',
    diagnosticos: ['Sem estrutura comercial — vendas dependem do acaso','Estrutura inicial — falta processo e previsibilidade','Estrutura parcial — time existe mas sem rotina clara','Máquina comercial — time autônomo e orientado a resultados'],
    recomendacoes: ['Defina metas claras e crie um processo básico de prospecção e atendimento.','Estruture uma rotina comercial diária com metas, atividades e acompanhamento.','Implemente reuniões de pipeline e acompanhe conversão por etapa do funil.','Automatize relatórios e foque em recrutamento e treinamento para expansão.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
  juridico: {
    label: 'Jurídico para Empresas', icon: 'ri-scales-3-line',
    cor: 'bg-indigo-600', corBg: 'bg-indigo-50', corText: 'text-indigo-700', corBar: '#4f46e5',
    diagnosticos: ['Exposição jurídica crítica — empresa vulnerável a conflitos','Baixa proteção jurídica — documentos improvisados e dispersos','Proteção parcial — organização existe mas com lacunas','Proteção jurídica sólida — empresa blindada e documentos em ordem'],
    recomendacoes: ['Formalize urgentemente contratos com clientes, fornecedores e colaboradores.','Centralize todos os documentos em uma pasta digital organizada por categoria.','Padronize contratos e crie uma biblioteca de documentos jurídicos de fácil acesso.','Implemente uma plataforma jurídica empresarial e revise contratos periodicamente.'],
    nivelSeveridade: ['Crítico','Alto','Médio','Baixo'],
  },
};

// ── MÓDULOS DO PROGRAMA TR60D POR ÁREA ─────────────────────────────────
interface Modulo {
  modulo: string;
  oQueVaiAprender: string[];
  beneficios: string[];
  transformacao: string;
  cor: string;
}

// Alinhado com os 7 módulos do site: Mapa de Gargalos | Gestão | Organização Financeira |
// Estrutura Comercial | Inovação e Eficiência | IA Aplicada | Jurídico (Bônus)
const modulosPrograma: Record<string, Modulo> = {
  financeiro: {
    modulo: 'Módulo 3 — Organização Financeira',
    oQueVaiAprender: [
      'Estruturação do fluxo de caixa',
      'DRE simplificado: organizar receitas, custos e despesas fixas',
      'Controle de custos e margem de contribuição',
      'Ponto de equilíbrio e formação de preço correta',
      'Ferramentas: planilha financeira estruturada, Dashboard Financeiro e Assistentes Financeiros com IA',
    ],
    beneficios: [
      'Visibilidade real de entradas e saídas todos os meses',
      'Decisões de investimento baseadas em dados concretos',
      'Capacidade de antecipar crises e planejar com segurança',
      'Planejamento Financeiro completo — saiba exatamente quanto vai lucrar',
    ],
    transformacao: 'De "não sei se vou fechar no vermelho" para "sei exatamente quanto vou lucrar este mês".',
    cor: 'bg-emerald-500',
  },
  vendas: {
    modulo: 'Módulo 4 — Estrutura Comercial',
    oQueVaiAprender: [
      'Funil comercial adaptado à sua operação',
      'Organização e qualificação de leads',
      'Processo de atendimento e avanço de oportunidades',
      'Scripts para abordagem, follow-up e fechamento',
      'Métricas para acompanhar conversão e performance',
    ],
    beneficios: [
      'Processo replicável por qualquer pessoa da equipe',
      'Previsibilidade de receita a partir de pipeline definido',
      'Redução do tempo entre primeiro contato e fechamento',
      'Plano prático da sua estrutura comercial',
    ],
    transformacao: 'De "vendas dependem da minha vontade" para "a máquina de vendas funciona independente de mim".',
    cor: 'bg-orange-500',
  },
  gestao: {
    modulo: 'Módulo 2 — Gestão e Estrutura Operacional',
    oQueVaiAprender: [
      'Definição de estrutura organizacional',
      'Mapeamento e documentação de processos',
      'Criação de rotinas operacionais claras',
      'Criação de indicadores de gestão e painel de acompanhamento',
      'Gestão por metas e reuniões de 15 minutos',
    ],
    beneficios: [
      'Equipe sabendo exatamente o que fazer sem perguntar para você',
      'Menos retrabalho e falhas por falta de comunicação',
      'Operação funcionando na sua ausência',
      'Estrutura organizacional clara e processos definidos',
    ],
    transformacao: 'De "se eu não fizer, não sai" para "a empresa funciona mesmo quando eu não estou".',
    cor: 'bg-sky-500',
  },
  indicadores: {
    // Indicadores é parte da Gestão — aqui referenciamos ao Módulo 2
    modulo: 'Módulo 2 — Gestão e Estrutura Operacional',
    oQueVaiAprender: [
      'Definição de estrutura organizacional',
      'Mapeamento e documentação de processos',
      'Criação de rotinas operacionais e painel de indicadores',
      'Gestão por metas e reuniões de acompanhamento',
      'B.I. com indicadores para decisões baseadas em dados',
    ],
    beneficios: [
      'Gestão orientada por dados, não por intuição',
      'Identificação precoce de problemas antes que agravem',
      'Visão integrada do negócio com indicadores-chave',
      'Estrutura organizacional clara e processos definidos',
    ],
    transformacao: 'De "acho que estou indo bem" para "sei exatamente como estou porque os números mostram".',
    cor: 'bg-sky-500',
  },
  dependencia: {
    // Dependência do dono mapeia para Gestão (Módulo 2) — técnicas de delegação
    modulo: 'Módulo 2 — Gestão e Estrutura Operacional',
    oQueVaiAprender: [
      'Técnicas de documentação para repassar atividades',
      'Como treinar pessoas para assumir processos operacionais',
      'Montar um manual de operações básico',
      'Rotina de gestão que reduz dependência no dia a dia',
      'Delegação que realmente funciona — libere seu tempo',
    ],
    beneficios: [
      'Tempo livre para focar em crescimento e estratégia',
      'Negócio que funciona sem sua presença física',
      'Valor do negócio aumenta ao se tornar independente do dono',
      'Mais qualidade de vida e menos estresse operacional',
    ],
    transformacao: 'De "o negócio sou eu" para "tenho uma empresa que funciona como sistema".',
    cor: 'bg-sky-500',
  },
  tecnologia: {
    modulo: 'Módulo 6 — Inteligência Artificial Aplicada ao Negócio',
    oQueVaiAprender: [
      'Uso de IA no atendimento ao cliente',
      'Geração automática de conteúdos',
      'Análise de dados com IA',
      'Automação de tarefas operacionais',
      'Criação de assistentes empresariais',
    ],
    beneficios: [
      'Redução de horas de trabalho manual com automações simples',
      'Atendimento mais rápido e profissional usando IA',
      'Processos 10x mais rápidos sem aumentar equipe',
      'Ferramentas de IA implementadas no seu negócio',
    ],
    transformacao: 'De "não tenho tempo para mexer com tecnologia" para "a tecnologia trabalha para mim".',
    cor: 'bg-rose-500',
  },
  time_comercial: {
    // Time Comercial é parte da Estrutura Comercial — Módulo 4
    modulo: 'Módulo 4 — Estrutura Comercial',
    oQueVaiAprender: [
      'Funil comercial adaptado à sua operação',
      'Organização e qualificação de leads',
      'Scripts para abordagem, follow-up e fechamento',
      'Métricas para acompanhar conversão e performance',
      'Etapas de fechamento com mais controle e previsibilidade',
    ],
    beneficios: [
      'Time comercial com direção clara e propósito definido',
      'Aumento de produtividade e conversão por vendedor',
      'Previsibilidade de novas vendas todo mês',
      'Plano prático da sua estrutura comercial',
    ],
    transformacao: 'De "só eu vendo" para "tenho uma máquina comercial que gera receita todos os dias".',
    cor: 'bg-orange-500',
  },
  juridico: {
    modulo: 'Módulo Bônus — Jurídico para Empresas',
    oQueVaiAprender: [
      'Onde as empresas mais erram na formalização de relações',
      'Contratos e documentos — como padronizar sem perder segurança',
      'O custo invisível da desorganização jurídica',
      'Riscos de depender de documentos soltos e processos manuais',
      'Biblioteca de documentos jurídicos e plataforma empresarial',
    ],
    beneficios: [
      'Proteção contra processos e passivos trabalhistas',
      'Segurança jurídica para fechar contratos maiores',
      'Organização que transmite profissionalismo para clientes',
      'Mapa de eficiência jurídica — reduza riscos e ganhe produtividade',
    ],
    transformacao: 'De "estou exposto a qualquer problema jurídico" para "minha empresa tem proteção e documentos em ordem".',
    cor: 'bg-indigo-600',
  },
};

function getDiagnosticoArea(areaKey: string, pontos: number, maxPontos: number): string {
  const cfg = areaDiagnosticoConfig[areaKey];
  if (!cfg) return '—';
  const pct = maxPontos > 0 ? pontos / maxPontos : 0;
  const idx = pct <= 0.25 ? 0 : pct <= 0.5 ? 1 : pct <= 0.75 ? 2 : 3;
  return cfg.diagnosticos[idx];
}

function getNivelSeveridade(areaKey: string, pontos: number, maxPontos: number): string {
  const cfg = areaDiagnosticoConfig[areaKey];
  if (!cfg) return '—';
  const pct = maxPontos > 0 ? pontos / maxPontos : 0;
  const idx = pct <= 0.25 ? 0 : pct <= 0.5 ? 1 : pct <= 0.75 ? 2 : 3;
  return cfg.nivelSeveridade[idx];
}

function getRecomendacaoArea(areaKey: string, pontos: number, maxPontos: number): string {
  const cfg = areaDiagnosticoConfig[areaKey];
  if (!cfg) return '—';
  const pct = maxPontos > 0 ? pontos / maxPontos : 0;
  const idx = pct <= 0.25 ? 0 : pct <= 0.5 ? 1 : pct <= 0.75 ? 2 : 3;
  return cfg.recomendacoes[idx];
}

function GaugeMeter({ posicao, size = 200 }: { posicao: number; size?: number }) {
  const cx = size * 0.7; const cy = size * 0.65; const r = size * 0.5;
  const angle = -180 + (posicao / 100) * 180;
  const rad = (angle * Math.PI) / 180;
  const px = cx + r * Math.cos(rad);
  const py = cy + r * Math.sin(rad);
  return (
    <div style={{ width: size, height: size * 0.575 }} className="relative mx-auto">
      <svg viewBox={`0 0 ${size * 1.4} ${size * 0.85}`} className="w-full h-full overflow-visible">
        <defs>
          <linearGradient id="gaugeGradDiag" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="45%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="20" strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="url(#gaugeGradDiag)" strokeWidth="20" strokeLinecap="round" />
        <line x1={cx} y1={cy} x2={px} y2={py} stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="10" fill="#f59e0b" />
        <circle cx={cx} cy={cy} r="5" fill="#374151" />
        <text x={size * 0.09} y={size * 0.825} fill="#9ca3af" fontSize="10" fontWeight="700" textAnchor="middle">Risco</text>
        <text x={size * 1.31} y={size * 0.825} fill="#9ca3af" fontSize="10" fontWeight="700" textAnchor="middle">Escala</text>
      </svg>
    </div>
  );
}

// ── GERAÇÃO DO HTML COMPLETO ─────────────────────────────────────────────
function gerarHTMLCompleto(lead: Lead, nivelCfg: typeof nivelConfig[number]): string {
  const d = lead.diagnostico!;

  // Mapeamento de cores hex para as áreas (equivalente aos valores Tailwind do modal)
  const areaCores: Record<string, { bg: string; icon: string; text: string }> = {
    financeiro:   { bg: '#ecfdf5', icon: '#10b981', text: '#065f46' },
    vendas:       { bg: '#fff7ed', icon: '#f97316', text: '#9a3412' },
    gestao:       { bg: '#f0f9ff', icon: '#0ea5e9', text: '#0369a1' },
    indicadores:  { bg: '#f5f3ff', icon: '#8b5cf6', text: '#7c3aed' },
    dependencia:  { bg: '#fef2f2', icon: '#ef4444', text: '#b91c1c' },
    tecnologia:   { bg: '#fff1f2', icon: '#f43f5e', text: '#e11d48' },
    time_comercial: { bg: '#fffbeb', icon: '#f59e0b', text: '#92400e' },
    juridico:     { bg: '#eef2ff', icon: '#4f46e5', text: '#4338ca' },
  };

  // Cores do módulo para DEPOIS
  const moduloCores: Record<string, { bg: string; border: string; text: string }> = {
    financeiro:   { bg: 'rgba(16,185,129,0.08)',  border: '#10b981', text: '#065f46' },
    vendas:       { bg: 'rgba(249,115,22,0.08)',  border: '#f97316', text: '#9a3412' },
    gestao:       { bg: 'rgba(14,165,233,0.08)',  border: '#0ea5e9', text: '#0369a1' },
    indicadores:  { bg: 'rgba(139,92,246,0.08)',  border: '#8b5cf6', text: '#7c3aed' },
    dependencia:  { bg: 'rgba(239,68,68,0.08)',    border: '#ef4444', text: '#b91c1c' },
    tecnologia:   { bg: 'rgba(244,63,94,0.08)',   border: '#f43f5e', text: '#e11d48' },
    time_comercial: { bg: 'rgba(245,158,11,0.08)', border: '#f59e0b', text: '#92400e' },
    juridico:     { bg: 'rgba(79,70,229,0.08)',   border: '#4f46e5', text: '#4338ca' },
  };

  // Emojis corretos para cada área (equivalente aos ícones Remix do modal)
  const areaIconEmojis: Record<string, string> = {
    financeiro:    '💰',
    vendas:        '🤝',
    gestao:        '⚙️',
    indicadores:   '📊',
    dependencia:   '👤',
    tecnologia:     '🤖',
    time_comercial: '🌟',
    juridico:       '⚖️',
  };

  const areasHTML = d.areas.map((area) => {
    const cfg = areaDiagnosticoConfig[area.areaKey];
    const modulo = modulosPrograma[area.areaKey];
    if (!cfg) return '';
    const pct = area.maxPontos > 0 ? Math.round((area.pontos / area.maxPontos) * 100) : 0;
    const nivelSev = getNivelSeveridade(area.areaKey, area.pontos, area.maxPontos);

    // Cores por severidade
    const sevCores = {
      'Crítico': { bg: '#fef2f2', border: '#fecaca', textBadge: '#b91c1c', bgBadge: '#fee2e2', textLabel: '#991b1b' },
      'Alto':    { bg: '#fff7ed', border: '#fed7aa', textBadge: '#c2410c', bgBadge: '#ffedd5', textLabel: '#7c2d12' },
      'Médio':   { bg: '#fffbeb', border: '#fde68a', textBadge: '#b45309', bgBadge: '#fef3c7', textLabel: '#92400e' },
      'Baixo':   { bg: '#f0fdf4', border: '#bbf7d0', textBadge: '#15803d', bgBadge: '#dcfce7', textLabel: '#14532d' },
    };
    const sev = sevCores[nivelSev as keyof typeof sevCores] || sevCores['Baixo'];
    const corArea = areaCores[area.areaKey] || areaCores['financeiro'];
    const corModulo = modulo ? (moduloCores[area.areaKey] || moduloCores['financeiro']) : null;

    return `
    <div class="area-card" style="border:1px solid #e2e8f0;border-radius:16px;overflow:hidden;margin-bottom:20px;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
      <!-- Header colorido como no modal -->
      <div style="background:${corArea.bg};border-bottom:1px solid rgba(0,0,0,0.05);padding:16px 20px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;">
          <div style="display:flex;align-items:center;gap:12px;flex:1;min-width:200px;">
            <div style="width:40px;height:40px;border-radius:10px;background:${corArea.icon};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              <span style="font-size:18px;">${areaIconEmojis[area.areaKey] || '📋'}</span>
            </div>
            <div>
              <p style="font-weight:800;font-size:15px;color:${corArea.text};margin-bottom:2px;">${cfg.label}</p>
              <p style="font-size:12px;color:#64748b;">${area.pontos}/${area.maxPontos} pts</p>
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:10px;min-width:160px;">
              <div style="flex:1;height:8px;background:rgba(255,255,255,0.8);border-radius:99px;overflow:hidden;">
                <div style="height:100%;width:${pct}%;background:${cfg.corBar};border-radius:99px;"></div>
              </div>
              <span style="font-size:16px;font-weight:900;color:${cfg.corBar};min-width:40px;text-align:right;">${pct}%</span>
            </div>
            <span style="font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;background:${sev.bgBadge};color:${sev.textBadge};border:1px solid ${sev.border};">${nivelSev}</span>
            ${modulo ? `<span style="font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;background:${corArea.icon};color:white;">TR60D</span>` : ''}
          </div>
        </div>
      </div>

      <!-- Cards ANTES e DEPOIS -->
      <div style="padding:16px 20px;background:white;">

        <!-- ANTES — com cor de severidade -->
        <div style="background:${sev.bg};border:1px solid ${sev.border};border-radius:12px;padding:14px 16px;margin-bottom:12px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
            <span style="font-size:14px;">⚠️</span>
            <span style="font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:0.5px;color:${sev.textLabel};">ANTES — Situação Atual</span>
          </div>
          <p style="font-size:13px;color:#374151;margin-bottom:10px;font-weight:500;line-height:1.55;">${area.diagnostico || getDiagnosticoArea(area.areaKey, area.pontos, area.maxPontos)}</p>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <span style="font-size:11px;font-weight:700;padding:4px 10px;border-radius:99px;background:${sev.bgBadge};color:${sev.textBadge};border:1px solid ${sev.border};">⚠ Gravidade: ${nivelSev}</span>
            <span style="font-size:11px;font-weight:600;padding:4px 10px;border-radius:99px;background:#fef3c7;color:#92400e;border:1px solid #fde68a;">💡 ${getRecomendacaoArea(area.areaKey, area.pontos, area.maxPontos)}</span>
          </div>
        </div>

        <!-- DEPOIS -->
        ${modulo ? `
        <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:14px 16px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
            <span style="font-size:14px;">✨</span>
            <span style="font-size:10px;font-weight:900;text-transform:uppercase;letter-spacing:0.5px;color:#166534;">DEPOIS — O que o TR60D resolve</span>
          </div>
          <p style="font-size:13px;font-weight:800;color:#14532d;margin-bottom:12px;">📚 ${modulo.modulo}</p>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:12px;">
            <div>
              <p style="font-size:10px;font-weight:800;color:#166534;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">O que você vai aprender:</p>
              ${modulo.oQueVaiAprender.map(item => `<div style="display:flex;gap:6px;align-items:flex-start;margin-bottom:4px;font-size:12px;color:#14532d;line-height:1.4;"><span style="color:#10b981;font-weight:900;flex-shrink:0;">✓</span><span>${item}</span></div>`).join('')}
            </div>
            <div>
              <p style="font-size:10px;font-weight:800;color:#166534;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Benefícios concretos:</p>
              ${modulo.beneficios.map(b => `<div style="display:flex;gap:6px;align-items:flex-start;margin-bottom:4px;font-size:12px;color:#14532d;line-height:1.4;"><span style="color:#f59e0b;font-weight:900;flex-shrink:0;">★</span><span>${b}</span></div>`).join('')}
            </div>
          </div>
          <div style="padding:10px 14px;background:${corModulo?.bg};border-left:4px solid ${corModulo?.border};border-radius:8px;">
            <p style="font-size:12px;font-weight:700;color:${corModulo?.text};line-height:1.5;">⚡ Transformação: ${modulo.transformacao}</p>
          </div>
        </div>
        ` : ''}
      </div>
    </div>`;
  }).join('');

  const whatsappCtaUrl = `https://wa.me/5592984398338?text=${encodeURIComponent('Recebi e analisei o diagnóstico TR60D e gostaria de saber mais sobre o programa!')}`;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Diagnóstico Empresarial — ${lead.nome}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif; background:#f1f5f9; color:#1e293b; }
  .container { max-width:900px; margin:0 auto; padding:24px 20px; }

  /* HEADER — light orange style */
  .header-top { background:linear-gradient(135deg,#f59e0b,#d97706);border-radius:20px 20px 0 0;padding:0;text-align:center;overflow:hidden; }
  .header-banner { width:100%;height:auto;display:block; }
  .header-content { padding:20px 32px 24px;text-align:center;background:linear-gradient(135deg,#f59e0b,#d97706); }

  /* CARD DE DIAGNÓSTICO — destaque */
  .diagnostico-card { background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.4);border-radius:16px;padding:20px 24px;margin:16px auto 0;max-width:600px;backdrop-filter:blur(8px);box-shadow:0 4px 20px rgba(0,0,0,0.15); }
  .cliente-nome { font-size:22px;font-weight:900;color:#ffffff;margin-bottom:4px;letter-spacing:-0.3px; }
  .cliente-empresa { font-size:13px;color:rgba(255,255,255,0.8);margin-bottom:16px;font-weight:500; }
  .stats-row { display:flex;justify-content:center;gap:12px;flex-wrap:wrap;margin-bottom:12px; }
  .stat-item { background:rgba(255,255,255,0.95);border-radius:12px;padding:10px 18px;min-width:90px;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,0.1); }
  .stat-label { font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:3px; }
  .stat-value { font-size:18px;font-weight:900;color:#1e293b;line-height:1; }
  .stat-value span { font-size:12px;font-weight:600;color:#94a3b8; }
  .nivel-label { font-size:12px;font-weight:800;color:#ffffff;background:rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.5);padding:5px 14px;border-radius:99px;display:inline-block;margin-top:4px;letter-spacing:0.3px; }

  /* BODY CONTENT */
  .content-bg { background:#ffffff;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 20px 20px;padding:32px 36px; }

  /* RESUMO */
  .resumo-card { background:#fff7ed;border:1px solid #fed7aa;border-radius:16px;padding:24px;margin-bottom:24px; }
  .resumo-mensagem { font-size:15px;font-style:italic;color:#7c2d12;margin-bottom:16px;font-weight:600;line-height:1.5; }
  .problemas-title { font-size:11px;font-weight:800;color:#9a3412;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px; }
  .problema-item { display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;font-size:13px;color:#7c2d12; }
  .problema-item span:first-child { color:#dc2626;flex-shrink:0; }

  /* CTA */
  .cta-section { background:linear-gradient(135deg,#f59e0b,#d97706);border-radius:16px;padding:24px;text-align:center;margin:24px 0; }
  .cta-section h2 { font-size:18px;font-weight:900;color:#ffffff;margin-bottom:6px; }
  .cta-section p { font-size:13px;color:#ffffff;opacity:0.9;line-height:1.5; }

  /* SECTION TITLE */
  .section-title { font-size:13px;font-weight:900;color:#0f172a;text-transform:uppercase;letter-spacing:1px;margin-bottom:16px;padding-bottom:8px;border-bottom:2px solid #f59e0b;display:flex;align-items:center;gap:8px; }

  /* AREA CARDS - agora usando inline styles */

  /* WHATSAPP CTA */
  .wa-cta { display:inline-block;width:100%;background:linear-gradient(135deg,#25D366,#128C7E);color:white;border:none;border-radius:14px;padding:18px 24px;text-align:center;text-decoration:none;margin-top:8px; }
  .wa-cta .wa-title { font-size:16px;font-weight:900;display:block;margin-bottom:4px; }
  .wa-cta .wa-sub { font-size:12px;opacity:0.9;display:block; }

  /* FOOTER */
  .footer { text-align:center;padding:20px 0 8px; }
  .footer-brand { font-size:11px;font-weight:700;color:#ffffff;margin-bottom:4px;opacity:0.85; }
  .footer-date { font-size:11px;color:rgba(255,255,255,0.6); }

  @media (max-width:700px) {
    .header-top { padding:20px; }
    .content-bg { padding:20px; }
    .container { padding:16px; }
  }
</style>
</head>
<body>
<div class="container">

  <!-- HEADER -->
  <div class="header-top">
    <img src="data:image/png;base64,${tr60dHeaderB64}" alt="TR60D" class="header-banner" />
    <div class="header-content">
      <p class="header-eyebrow">Diagnóstico gerado pela Plataforma TR60D · Transformação Real 60D</p>

      <!-- Card de Diagnóstico em destaque -->
      <div class="diagnostico-card">
        <p class="cliente-nome">${lead.nome}</p>
        <p class="cliente-empresa">${lead.empresa}</p>
        <div class="stats-row">
          <div class="stat-item">
            <p class="stat-label">Nível</p>
            <p class="stat-value">${nivelCfg.emoji} ${lead.diagnostico.nivelNumero}</p>
          </div>
          <div class="stat-item">
            <p class="stat-label">Pontuação</p>
            <p class="stat-value">${d.pontuacaoTotal}<span>/${d.pontuacaoMaxima}</span></p>
          </div>
          <div class="stat-item">
            <p class="stat-label">Percentual</p>
            <p class="stat-value">${d.percentualGeral}<span>%</span></p>
          </div>
        </div>
        <p class="nivel-label">${nivelCfg.titulo} — ${nivelCfg.subtitulo}</p>
      </div>
    </div>
  </div>

  <!-- BODY -->
  <div class="content-bg">

    <!-- RESUMO DO DIAGNÓSTICO -->
    <div class="resumo-card">
      <p class="resumo-mensagem">"${nivelCfg.mensagem}"</p>
      <p class="problemas-title">Desafios identificados no seu negócio:</p>
      ${nivelCfg.problemas.map(p => `<div class="problema-item"><span>⚠</span><span>${p}</span></div>`).join('')}
    </div>

    <!-- CTA -->
    <div class="cta-section">
      <h2>🚀 Transformação Real 60D</h2>
      <p>Programa de mentoria presencial 100% prático para resolver os desafios identificados. 60 dias com acompanhamento de especialista.</p>
    </div>

    <!-- RELATÓRIO ANTES / DEPOIS POR ÁREA -->
    <div class="section-title">📋 Relatório Completo por Área</div>
    ${areasHTML}

    <!-- BOTAO WHATSAPP -->
    <a href="${whatsappCtaUrl}" target="_blank" rel="noopener noreferrer" class="wa-cta">
      <span class="wa-title">🚀 Quero participar do TR60D</span>
      <span class="wa-sub">Clique aqui para falar diretamente pelo WhatsApp</span>
    </a>

    <!-- FOOTER -->
    <div class="footer">
      <p class="footer-brand">Diagnóstico gerado pela Plataforma TR60D · Transformação Real 60D</p>
      <p class="footer-date">Gerado em ${new Date().toLocaleDateString('pt-BR')}</p>
    </div>

  </div>
</div>
</body>
</html>`;
}

function gerarMensagemWhatsApp(lead: Lead, nivelCfg: typeof nivelConfig[number]): string {
  const d = lead.diagnostico!;
  let msg = ``;
  // ── Mensagem introdutória ──
  msg += `👋 Olá, *${lead.nome}*! Tudo bem?\n\n`;
  msg += `Segue abaixo o seu *Diagnóstico Empresarial* completo da Transformação Real 60D.\n\n`;
  msg += `Este relatório apresenta um panorama geral dos principais desafios identificados no seu negócio, seguido pelo relatório detalhado *ANTES / DEPOIS* de cada área avaliada — mostrando onde você está hoje e como podemos chegar lá.\n\n`;
  msg += `${'─'.repeat(30)}\n\n`;
  // ── Diagnóstico geral ──
  msg += `🔥 *DIAGNÓSTICO EMPRESARIAL — ${lead.nome}*\n\n`;
  msg += `${nivelCfg.emoji} *${nivelCfg.titulo}*\n`;
  msg += `${nivelCfg.subtitulo}\n\n`;
  msg += `📊 Resultado: ${d.pontuacaoTotal}/${d.pontuacaoMaxima} pontos (${d.percentualGeral}%)\n\n`;
  msg += `💡 *"${nivelCfg.mensagem}"*\n\n`;
  msg += `⚠️ *Desafios identificados:*\n`;
  nivelCfg.problemas.forEach((p) => { msg += `• ${p}\n`; });
  msg += `\n${'─'.repeat(30)}\n`;
  msg += `\n📋 *RELATÓRIO ANTES / DEPOIS:*\n`;
  msg += `${'─'.repeat(30)}\n`;
  d.areas.forEach((area) => {
    const cfg = areaDiagnosticoConfig[area.areaKey];
    const modulo = modulosPrograma[area.areaKey];
    if (!cfg) return;
    const pct = area.maxPontos > 0 ? Math.round((area.pontos / area.maxPontos) * 100) : 0;
    msg += `\n*${cfg.label}* (${area.pontos}/${area.maxPontos} pts — ${pct}%)\n`;
    msg += `  ANTES: ${area.diagnostico || getDiagnosticoArea(area.areaKey, area.pontos, area.maxPontos)}\n`;
    if (modulo) {
      msg += `  DEPOIS: ${modulo.transformacao}\n`;
    }
  });
  msg += `\n${'─'.repeat(30)}\n`;
  // ── Fechamento ──
  msg += `\n🚀 *TRANSFORMAÇÃO REAL 60D*\n`;
  msg += `Programa de mentoria presencial 100% prático para resolver cada um desses pontos.\n\n`;
  msg += `👉 Quer receber uma proposta personalizada para o seu negócio? É só responder aqui ou agendar uma conversa connosco!`;
  return msg;
}

export default function GerarDiagnosticoModal({ lead, onClose }: GerarDiagnosticoModalProps) {
  const [copiado, setCopiado] = useState(false);
  const [htmlGerado, setHtmlGerado] = useState(false);
  const [interessadoSync, setInteressadoSync] = useState<'loading'|'success'|'error'|'idle'>('idle');

  // Marca o lead como Interessado no TR60D ao abrir o modal
  useEffect(() => {
    if (lead?.email) {
      setInteressadoSync('loading');
      markAsInteressado(lead.email).then((result) => {
        setInteressadoSync(result.success ? 'success' : 'error');
        setTimeout(() => setInteressadoSync('idle'), 3000);
      });
    }
  }, [lead?.email]);

  if (!lead.diagnostico) return null;

  const nivelCfg = nivelConfig[lead.diagnostico.nivelNumero];
  const whatsappNumer = lead.whatsapp.replace(/\D/g, '');
  const mensagemWhatsApp = gerarMensagemWhatsApp(lead, nivelCfg);
  const whatsappUrl = `https://wa.me/55${whatsappNumer}?text=${encodeURIComponent(mensagemWhatsApp)}`;

  const handleCopiar = () => {
    navigator.clipboard.writeText(mensagemWhatsApp).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  const handleGerarHTML = () => {
    const html = gerarHTMLCompleto(lead, nivelCfg);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostico-${lead.nome.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.html`;
    a.click();
    URL.revokeObjectURL(url);
    setHtmlGerado(true);
    setTimeout(() => setHtmlGerado(false), 3000);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">

        {/* HEADER */}
        <div className="px-6 pt-6 pb-0 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-100">
                <i className="ri-file-chart-line text-amber-600 text-xl" />
              </div>
              <div>
                <h2 className="text-gray-900 font-black text-lg">Relatório ANTES / DEPOIS — {lead.nome}</h2>
                <p className="text-gray-400 text-sm">{lead.empresa}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-400 text-lg" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full ${nivelCfg.bgCard} ${nivelCfg.textColor} border ${nivelCfg.borderColor}`}>
              {nivelCfg.emoji} Nível {lead.diagnostico.nivelNumero} — {nivelCfg.titulo}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-gray-100 text-gray-600">
              <i className="ri-bar-chart-2-line text-xs" />{lead.diagnostico.percentualGeral}% geral
            </span>
            {lead.whatsapp && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-600">
                <i className="ri-whatsapp-line text-xs" />{lead.whatsapp}
              </span>
            )}
            {interessadoSync === 'loading' && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-blue-50 text-blue-600">
                <i className="ri-loader-2-line text-xs animate-spin" />Sincronizando...
              </span>
            )}
            {interessadoSync === 'success' && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
                <i className="ri-check-line text-xs" />Interessado TR60D
              </span>
            )}
            {interessadoSync === 'error' && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-red-50 text-red-600">
                <i className="ri-error-line text-xs" />Erro Sync
              </span>
            )}
          </div>
        </div>

        {/* CONTEÚDO */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4 bg-gray-50">

          {/* Resumo Geral */}
          <div className={`${nivelCfg.bgCard} border-2 ${nivelCfg.borderColor} rounded-2xl p-5`}>
            <div className="flex flex-col sm:flex-row items-center gap-5">
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <span className="text-3xl">{nivelCfg.emoji}</span>
                  <span className={`text-xs font-bold uppercase tracking-widest ${nivelCfg.textColor}`}>Nível {lead.diagnostico.nivelNumero}</span>
                </div>
                <p className={`font-black text-2xl leading-tight mb-1 ${nivelCfg.textColor}`}>{nivelCfg.titulo}</p>
                <p className="text-gray-500 text-sm mb-4">{nivelCfg.subtitulo}</p>
                <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
                  <div className="bg-white rounded-xl px-4 py-2.5 text-center border border-white">
                    <p className="text-gray-400 text-xs">Pontuação</p>
                    <p className={`font-black text-xl ${nivelCfg.textColor}`}>{lead.diagnostico.pontuacaoTotal}<span className="text-gray-400 text-sm font-normal">/{lead.diagnostico.pontuacaoMaxima}</span></p>
                  </div>
                  <div className="bg-white rounded-xl px-4 py-2.5 text-center border border-white">
                    <p className="text-gray-400 text-xs">Percentual</p>
                    <p className={`font-black text-xl ${nivelCfg.textColor}`}>{lead.diagnostico.percentualGeral}%</p>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">
                <GaugeMeter posicao={nivelCfg.posicao} size={180} />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-black/10">
              <p className={`text-xs font-bold italic ${nivelCfg.textColor} mb-3`}>&ldquo;{nivelCfg.mensagem}&rdquo;</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {nivelCfg.problemas.map((prob) => (
                  <div key={prob} className="flex items-start gap-2">
                    <div className={`w-4 h-4 flex items-center justify-center rounded-full ${nivelCfg.badgeBg} flex-shrink-0 mt-0.5`}>
                      <i className={`ri-alert-line ${nivelCfg.textColor} text-xs`} />
                    </div>
                    <p className="text-gray-600 text-xs leading-relaxed">{prob}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ANTES / DEPOIS POR ÁREA */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-amber-500">
                <i className="ri-book-2-line text-white text-xs" />
              </div>
              <p className="text-gray-700 text-sm font-black uppercase tracking-wider">Relatório ANTES / DEPOIS — O que o TR60D resolve</p>
            </div>
            <div className="space-y-4">
              {lead.diagnostico.areas.map((area) => {
                const cfg = areaDiagnosticoConfig[area.areaKey];
                const modulo = modulosPrograma[area.areaKey];
                if (!cfg) return null;
                const pct = area.maxPontos > 0 ? Math.round((area.pontos / area.maxPontos) * 100) : 0;
                const nivelSev = getNivelSeveridade(area.areaKey, area.pontos, area.maxPontos);
                const corSev = nivelSev === 'Crítico' ? 'bg-red-50 border-red-200' : nivelSev === 'Alto' ? 'bg-orange-50 border-orange-200' : nivelSev === 'Médio' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
                const corSevText = nivelSev === 'Crítico' ? 'text-red-600' : nivelSev === 'Alto' ? 'text-orange-600' : nivelSev === 'Médio' ? 'text-amber-600' : 'text-emerald-600';
                const corSevBg = nivelSev === 'Crítico' ? 'bg-red-100 text-red-700' : nivelSev === 'Alto' ? 'bg-orange-100 text-orange-700' : nivelSev === 'Médio' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';

                return (
                  <div key={area.areaKey} className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">

                    {/* Header da área */}
                    <div className={`${cfg.corBg} border-b border-black/5 p-4`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${cfg.cor}`}>
                            <i className={`${cfg.icon} text-white text-sm`} />
                          </div>
                          <div>
                            <span className={`font-black text-sm ${cfg.corText}`}>{cfg.label}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-gray-400 text-xs">{area.pontos}/{area.maxPontos} pts</span>
                              <div className="w-24 h-1.5 bg-white/70 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${cfg.corBar}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className={`text-xs font-black ${cfg.corText}`}>{pct}%</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${corSevBg}`}>{nivelSev}</span>
                          {modulo && (
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${modulo.cor} text-white hidden sm:inline-flex items-center gap-1`}>
                              <i className="ri-graduation-cap-line text-xs" />TR60D
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 space-y-3">

                      {/* ANTES */}
                      <div className={`${corSev} border rounded-xl p-4`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-base">⚠️</span>
                          <span className={`text-xs font-black uppercase tracking-widest ${corSevText}`}>ANTES — Situação Atual</span>
                        </div>
                        <p className="text-gray-700 text-sm font-semibold leading-relaxed mb-3">
                          {area.diagnostico || getDiagnosticoArea(area.areaKey, area.pontos, area.maxPontos)}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${corSevBg}`}>
                            Gravidade: {nivelSev}
                          </span>
                          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            {getRecomendacaoArea(area.areaKey, area.pontos, area.maxPontos)}
                          </span>
                        </div>
                      </div>

                      {/* DEPOIS */}
                      {modulo && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-base">✨</span>
                            <span className="text-xs font-black uppercase tracking-widest text-emerald-700">DEPOIS — O que o TR60D resolve</span>
                          </div>
                          <p className="font-bold text-emerald-800 text-sm mb-3">📚 {modulo.modulo}</p>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                            <div>
                              <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2">O que você vai aprender:</p>
                              {modulo.oQueVaiAprender.map((item) => (
                                <div key={item} className="flex items-start gap-2 mb-1.5">
                                  <i className={`ri-checkbox-circle-line text-emerald-500 text-xs mt-0.5 flex-shrink-0`} />
                                  <p className="text-gray-600 text-xs leading-relaxed">{item}</p>
                                </div>
                              ))}
                            </div>
                            <div>
                              <p className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2">Benefícios concretos:</p>
                              {modulo.beneficios.map((b) => (
                                <div key={b} className="flex items-start gap-2 mb-1.5">
                                  <i className={`ri-arrow-right-circle-line text-emerald-500 text-xs mt-0.5 flex-shrink-0`} />
                                  <p className="text-gray-600 text-xs leading-relaxed">{b}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className={`${modulo.cor} bg-opacity-10 border border-opacity-30 ${modulo.cor.replace('bg-', 'border-')} rounded-xl p-3`}>
                            <p className={`${modulo.cor.replace('bg-', 'text-')} text-xs font-bold leading-relaxed`}>
                              ⚡ Transformação: {modulo.transformacao}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA do Programa */}
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/20 mx-auto mb-3">
              <i className="ri-rocket-line text-white text-2xl" />
            </div>
            <h3 className="text-white font-black text-xl mb-2">Transformação Real 60D</h3>
            <p className="text-amber-100 text-sm leading-relaxed mb-4 max-w-lg mx-auto">
              Programa de mentoria presencial 100% prático para resolver cada desafio listado acima. 60 dias com acompanhamento de especialista.
            </p>
            <div className="flex flex-wrap justify-center gap-2 text-xs text-amber-100">
              <span className="bg-white/10 px-3 py-1 rounded-full"><i className="ri-calendar-check-line mr-1" />60 dias de mentoria</span>
              <span className="bg-white/10 px-3 py-1 rounded-full"><i className="ri-user-star-line mr-1" />Presencial com especialista</span>
              <span className="bg-white/10 px-3 py-1 rounded-full"><i className="ri-tools-line mr-1" />Método 100% prático</span>
            </div>
          </div>

                  </div>

        {/* FOOTER — Ações */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Gerar HTML */}
            <button
              onClick={handleGerarHTML}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {htmlGerado ? (
                <><i className="ri-check-line text-emerald-300" /><span className="text-emerald-300">HTML gerado!</span></>
              ) : (
                <><i className="ri-file-html5-line text-lg" /><span>Gerar página HTML</span></>
              )}
            </button>

            {/* Copiar mensagem */}
            <button
              onClick={handleCopiar}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {copiado ? (
                <><i className="ri-check-line text-emerald-500" /><span className="text-emerald-600">Mensagem copiada!</span></>
              ) : (
                <><i className="ri-file-copy-line" /><span>Copiar mensagem</span></>
              )}
            </button>

            {/* WhatsApp */}
            {lead.whatsapp ? (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                <span>Enviar via WhatsApp</span>
              </a>
            ) : (
              <button
                disabled
                className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-400 font-bold text-sm py-3.5 rounded-xl cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg" />
                <span>Sem WhatsApp</span>
              </button>
            )}
          </div>
          <p className="text-gray-400 text-xs text-center mt-3">
            Gere o HTML para enviar por e-mail ou salvar e enviar ao lead. A mensagem inclui ANTES/DEPOIS completo.
          </p>
        </div>
      </div>
    </div>
  );
}
